import { useEffect, useState } from "react";
import { Timer } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";

/** Duration in seconds for each candle timeframe */
const TF_SECONDS = {
  "1m": 60, "5m": 300, "15m": 900, "30m": 1800,
  "1h": 3600, "4h": 14400, "1d": 86400,
};

/** Compute UTC-anchored seconds remaining until the next candle closes.
 * For daily we use UTC midnight; for smaller frames we align to Unix epoch. */
function secondsToNext(timeframe) {
  const now = Date.now() / 1000;
  const tf = TF_SECONDS[timeframe] || 300;
  return tf - Math.floor(now % tf);
}

function pad(n) { return String(n).padStart(2, "0"); }

/** Format seconds as mm:ss (or hh:mm:ss when > 1h) */
function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
}

export default function CandleCountdown({ timeframe, compact = false }) {
  const { t } = useI18n();
  const [remaining, setRemaining] = useState(() => secondsToNext(timeframe));

  useEffect(() => {
    setRemaining(secondsToNext(timeframe));
    const id = setInterval(() => setRemaining(secondsToNext(timeframe)), 1000);
    return () => clearInterval(id);
  }, [timeframe]);

  const total = TF_SECONDS[timeframe] || 300;
  const pct = ((total - remaining) / total) * 100;
  // colour speeds up near close
  const critical = remaining < total * 0.1;
  const color = critical ? "text-red-400" : remaining < total * 0.3 ? "text-amber-400" : "text-emerald-400";

  if (compact) {
    return (
      <div
        className={cn("inline-flex items-center gap-1.5 font-bold num text-xs", color)}
        data-testid="candle-countdown"
      >
        <Timer className={cn("h-3 w-3", critical && "animate-pulse")} />
        <span>{fmt(remaining)}</span>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="candle-countdown">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-zinc-500 flex items-center gap-1">
          <Timer className={cn("h-3 w-3", critical && "animate-pulse")} />
          {t("candle_closes_in")}
        </span>
        <span className={cn("num font-bold", color)}>{fmt(remaining)}</span>
      </div>
      <div className="h-0.5 bg-zinc-800 rounded-sm overflow-hidden" dir="ltr">
        <div
          className={cn("h-full transition-all duration-1000 ease-linear",
            critical ? "bg-red-500" : remaining < total * 0.3 ? "bg-amber-500" : "bg-emerald-500"
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
